import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * A custom element that displays free shipping progress.
 * Updates automatically when the cart changes.
 *
 * @extends {Component}
 */
class ProductFreeShipping extends Component {
  #abortController = new AbortController();
  #updateTimeout = null;
  #requestController = null;

  connectedCallback() {
    super.connectedCallback();
    this.#abortController.abort();
    this.#abortController = new AbortController();
    
    const { signal } = this.#abortController;
    
    // Listen for cart updates (this catches both CartAddEvent and CartUpdateEvent)
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, { signal });
    
    // Initial update with a small delay to ensure DOM is ready
    this.#updateTimeout = setTimeout(() => {
      this.#updateTimeout = null;
      this.#updateShippingMessage();
    }, 200);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    this.#requestController?.abort();
    
    // Clear any pending timeouts
    if (this.#updateTimeout) {
      clearTimeout(this.#updateTimeout);
      this.#updateTimeout = null;
    }
  }

  updatedCallback() {
    super.updatedCallback();
    this.#handleCartUpdate();
  }

  /**
   * Handles cart update events
   * @param {Event} [event] - The cart update event
   */
  #handleCartUpdate = async (event) => {
    this.#requestController?.abort();
    // Cancel any pending updates
    if (this.#updateTimeout) {
      clearTimeout(this.#updateTimeout);
    }
    
    // Wait a bit for the cart to be fully updated
    // Use a longer delay to ensure the cart API has the latest data
    this.#updateTimeout = setTimeout(() => {
      this.#updateTimeout = null;
      this.#updateShippingMessage();
    }, 400);
  };

  /**
   * Updates the shipping message based on current cart total
   */
  async #updateShippingMessage() {
    this.#requestController?.abort();
    const controller = new AbortController();
    this.#requestController = controller;
    try {
      // Fetch current cart with cache busting
      const url = new URL(`${window.Shopify?.routes?.root || '/'}cart.js`, window.location.origin);
      url.searchParams.set('_', String(Date.now()));
      const response = await fetch(url, { signal: controller.signal });
      if (!response.ok) {
        console.warn('Failed to fetch cart:', response.status);
        return;
      }
      const cart = await response.json();
      if (controller.signal.aborted || !this.isConnected) return;
      
      const threshold = parseInt(this.dataset.freeShippingThreshold, 10);
      if (isNaN(threshold)) {
        console.warn('Invalid free shipping threshold:', this.dataset.freeShippingThreshold);
        return;
      }
      
      const cartTotal = cart.total_price || 0;
      const remaining = threshold - cartTotal;
      const progress = threshold > 0 ? Math.min(Math.max((cartTotal / threshold) * 100, 0), 100) : 100;
      
      const progressElement = this.querySelector('.free-shipping-progress');
      const textElement = this.querySelector('.free-shipping-progress__message');
      const trackElement = this.querySelector('.free-shipping-progress__track');
      const percentElement = this.querySelector('.free-shipping-progress__percent');
      
      if (!progressElement || !textElement) {
        console.warn('Message elements not found');
        return;
      }

      progressElement.style.setProperty('--free-shipping-progress', `${progress}%`);
      if (trackElement) {
        trackElement.setAttribute('aria-valuenow', String(Math.round(progress)));
      }
      if (percentElement) {
        percentElement.textContent = `${Math.round(progress)}%`;
      }
      
      if (remaining > 0) {
        // Still need more to qualify
        const remainingMoney = this.#formatMoney(remaining);
        const customMessage = this.dataset.customMessage || '';
        const message = customMessage.replace(/\[REMAINING\]/g, remainingMoney);
        
        progressElement.classList.remove('free-shipping-progress--qualified');
        textElement.textContent = message;
      } else {
        // Qualified for free shipping
        const qualifiedMessage = this.dataset.qualifiedMessage || '';
        
        progressElement.classList.add('free-shipping-progress--qualified');
        textElement.textContent = qualifiedMessage;
      }
    } catch (error) {
      if (controller.signal.aborted) return;
      console.error('Error updating free shipping message:', error);
    }
  }

  /**
   * Formats money amount in cents to currency string
   * @param {number} cents - Amount in cents
   * @returns {string} Formatted money string
   */
  #formatMoney(cents) {
    // Get currency format from Shopify
    const currencyCode = window.Shopify?.currency?.active || window.Shopify?.shop?.currency || 'EUR';
    const locale = document.documentElement.lang || 'es-ES';
    const amount = cents / 100;
    
    // Use Intl.NumberFormat for proper currency formatting
    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currencyCode,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount);
    } catch (e) {
      // Fallback if currency code is invalid
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount);
    }
  }
}

if (!customElements.get('product-free-shipping')) {
  customElements.define('product-free-shipping', ProductFreeShipping);
}
